import React, { useState, useCallback, useEffect } from 'react';
import { handleStringFilterChange } from './filters';
import FilterListIcon from '@material-ui/icons/FilterList';
import DropdownMenuFilter, { MenuItemWithImage } from '../../DropdownMenu/DropdownMenuFilter.component';
import { useStyles } from './filters.styles';
import { FilterProps, IFilterItem } from './filters.types';
import { IconButton } from '@material-ui/core';
import { useTabNavigationDetails } from '../../../Context/TabNavigation.context';
import TooltipOne from '../../TooltipOne/TooltipOne.component';
import { useLayoutContext } from '../../../Context/Layout.context';

interface ItemsFilterProps<T> extends FilterProps<T> {
  valueName: string;
  valueLabel: string;
  items?: string[];
  itemsWithImages?: MenuItemWithImage<T>[];
  onConvert?: (value: T) => T;
  itemsWithValues?: IFilterItem<T>[];
  disabled?: boolean;
  filterState?: T[];
  transformHorizontal?: 'left' | 'center' | 'right' | number;
  transformVertical?: 'top' | 'center' | 'bottom' | number;

  filter?: T | T[];
}

export const ItemsFilter = <T,>({
  valueName,
  valueLabel,
  onChange,
  onConvert,
  resetFilter,
  items,
  itemsWithValues,
  itemsWithImages,
  disabled,
  filterState,
  transformHorizontal,
  transformVertical,
  filter,
}: ItemsFilterProps<T>): JSX.Element => {
  const classes = useStyles();
  const { updateStableMenuAnchor, stableMenuAnchor } = useLayoutContext();

  const { tabIndexForChildren } = useTabNavigationDetails();

  const [anchorElItems, setAnchorElItems] = useState<SVGSVGElement | HTMLElement | null>(null);
  const [itemsFilter, setItemsFilter] = useState<T[]>([]);

  useEffect(() => {
    const clearFilter = Array.isArray(filter) ? filter : filter ? [filter] : undefined;
    if (clearFilter !== undefined) {
      setItemsFilter(clearFilter);
    }
  }, [filter]);

  useEffect(() => {
    if (resetFilter) {
      setItemsFilter([]);
    }
  }, [resetFilter]);

  useEffect(() => {
    if (filterState) {
      setItemsFilter(filterState);
    }
  }, [filterState]);

  const convertFilter = !itemsWithValues
    ? null
    : (value: T) => {
        const foundItem = itemsWithValues.find((item: IFilterItem<T>) => {
          return item.value === value;
        });
        if (foundItem) {
          return foundItem.value;
        } else {
          return null;
        }
      };

  const handleItemsFilterChange = useCallback(
    (value?: T) => {
      handleStringFilterChange(
        itemsFilter,
        (newFilters: T[]) => {
          setItemsFilter(filterState ? filterState : newFilters);
          const convertedValue: T[] = [];
          newFilters.forEach((value: T) => {
            let conversionResult = null;
            if (onConvert) {
              conversionResult = onConvert(value);
            } else if (convertFilter) {
              conversionResult = convertFilter(value);
            } else {
              conversionResult = value;
            }
            if (conversionResult !== null) {
              convertedValue.push(conversionResult);
            }
          });
          onChange(valueName, convertedValue);
        },
        value,
      );
    },
    [itemsFilter, filterState, onChange],
  );

  const onOpenFilter = (
    event:
      | React.MouseEvent<SVGSVGElement, MouseEvent>
      | React.KeyboardEvent<SVGSVGElement>
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>,
  ) => {
    if (!anchorElItems) {
      const rect = event.currentTarget?.getBoundingClientRect();
      if (rect) {
        // moving stable menu anchor to current position of the real anchor element for the menu
        updateStableMenuAnchor(rect.left, rect.top);
        // and setting the stable anchor instead real one
        setAnchorElItems(event.currentTarget ? stableMenuAnchor.current : event.currentTarget);
      } else {
        setAnchorElItems(event.currentTarget);
      }
    } else setAnchorElItems(null);
  };

  return !disabled ? (
    <>
      <TooltipOne title={`Ustaw filtr do wyszukiwania: ${valueLabel}`} placement="top">
        <IconButton
          disableRipple
          aria-label={`Ikonka rozwijająca filtr ${valueLabel}`}
          size="small"
          onClick={onOpenFilter}
          tabIndex={-1}
        >
          <FilterListIcon
            className={itemsFilter.length ? classes.activeFilter : ''}
            aria-controls={anchorElItems ? `${valueLabel} menu` : undefined}
            aria-label={`Filtr ${valueLabel}`}
            aria-haspopup="true"
            aria-hidden="false"
            aria-expanded={anchorElItems ? 'true' : undefined}
            tabIndex={tabIndexForChildren}
          />
        </IconButton>
      </TooltipOne>
      <DropdownMenuFilter
        anchorEl={anchorElItems}
        setAnchorEl={setAnchorElItems}
        menuItems={items}
        menuItemsWithValue={
          itemsWithValues ? itemsWithValues.map((item: IFilterItem<T>) => [item.label, item.value]) : undefined
        }
        menuItemsWithImages={itemsWithImages}
        hasCheckbox={true}
        checkedItems={itemsFilter}
        onChange={handleItemsFilterChange}
        uncheckAllMenuItems={handleItemsFilterChange}
        transformOrigin={{
          vertical: transformVertical ?? 'top',
          horizontal: transformHorizontal ?? 0,
        }}
      />
    </>
  ) : (
    <FilterListIcon style={{ color: 'gray', pointerEvents: 'none' }} />
  );
};
